import { useCallback, useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import {
  FileDownloadOutlined,
  NorthEastOutlined,
  SouthWestOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { v4 as uuidv4 } from "uuid";

import useDataSourceManager from "hooks/useDataSourceManager";
import MainCard from "components/MainCard";
import ImportCreditsModal from "pages/LicenseManagement/Overview/components/ImportCreditsModal";
import RecentTransactionsBlock from "./components/RecentTransactionsBlock";
import Overview from "./components/Overview";
import UsageEmulator from "./components/UsageEmulator";
import TransferCreditsModal from "./components/TransferCreditsModal";
import ExportReportModal from "./components/ExportReportModal";
import EllipsisTooltipCell from "utils/ellipsisTooltipCell";
import styles from "./mainStyle.module.scss";
import licenseLayoutStyles from "pages/LicenseManagement/mainStyle.module.scss";
import btnOverview from "assets/images/icons/btn_overview.png";
import btnTransactions from "assets/images/icons/btn_transactions.png";
import btnUsageEmulator from "assets/images/icons/btn_emulator.png";
import btnWallet from "assets/images/icons/btn_subscriptions.png";

const formatNumber = (value) =>
  new Intl.NumberFormat("en-US").format(value || 0);

const getUsageAlert = ({
  alertStatus,
  dailyUsage,
  remainingDays,
  hasSubscription,
}) => {
  if (!hasSubscription) return "No Subscription";
  if (alertStatus === "DEDUCT_FAILED") return "Grace Period";
  if (remainingDays != null && remainingDays <= 0) return "Grace Period";
  if (dailyUsage <= 0 || remainingDays == null || remainingDays > 365) {
    return "Normal";
  }
  if (remainingDays > 90) return "Predictive Alert";
  if (remainingDays >= 30) return "Critical Alert";
  if (remainingDays > 0) return "Low Balance";
  return "Grace Period";
};

const actionItems = [
  {
    id: "disburse",
    label: "Transfer Credits",
    icon: SouthWestOutlined,
    transferDirection: "disburse",
  },
  {
    id: "withdraw",
    label: "Transfer Credits",
    icon: NorthEastOutlined,
    transferDirection: "withdraw",
  },
  {
    id: "export-report",
    label: "Export Report",
    icon: FileDownloadOutlined,
  },
];

const MspLicenseManagement = () => {
  const theme = useTheme();
  const { scopeReady } = useOutletContext();
  const { sendBatch } = useDataSourceManager();

  const [overview, setOverview] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [history, setHistory] = useState([]);
  const [mspName, setMspName] = useState(() => {
    try {
      return (
        JSON.parse(localStorage.getItem("selectBreadcrumbs") || "{}")?.realm
          ?.name || ""
      );
    } catch {
      return "";
    }
  });
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [error, setError] = useState("");
  const [openImportModal, setOpenImportModal] = useState(false);
  const [transferTarget, setTransferTarget] = useState(null);
  const [openExportReportModal, setOpenExportReportModal] = useState(false);
  const [exportTarget, setExportTarget] = useState(null);
  const [nowAnchor, setNowAnchor] = useState("overview");
  const loadedEntIdRef = useRef(null);

  const menuItems = [
    { id: "overview", label: "Overview", icon: btnOverview },
    { id: "transactions", label: "Transactions", icon: btnTransactions },
    { id: "usage-emulator", label: "Usage Emulator", icon: btnUsageEmulator },
    {
      id: "wallet-disbursement",
      label: "Wallet & Disbursement",
      icon: btnWallet,
    },
  ];

  const getMspEntId = () => {
    const selectedBreadcrumbs = JSON.parse(
      localStorage.getItem("selectBreadcrumbs") || "{}",
    );
    const entId = selectedBreadcrumbs?.realm?.id;

    if (!entId || selectedBreadcrumbs?.realm?.hasManageOrg !== true) {
      throw new Error("Select an MSP to view its license management data.");
    }

    return entId;
  };

  const fetchOverview = useCallback(
    async (force = false) => {
      if (!scopeReady) return;

      try {
        const entId = getMspEntId();
        const selectedBreadcrumbs = JSON.parse(
          localStorage.getItem("selectBreadcrumbs") || "{}",
        );
        setMspName(selectedBreadcrumbs?.realm?.name || "");

        // useDataSourceManager can change identity as shared app state updates.
        // Do not repeat the initial overview request for the same MSP as a result.
        if (!force && loadedEntIdRef.current === entId) return;

        loadedEntIdRef.current = entId;
        setLoadingOverview(true);
        setError("");
        const orgSummaryRequestId = uuidv4();
        const mspDashboardRequestId = uuidv4();
        const mspHistoryRequestId = uuidv4();
        const [summaryResponses, mspResponses] = await Promise.all([
          sendBatch([
            {
              apiKey: "/ents/:entId/orgs/license/summary.read",
              requestUuid: orgSummaryRequestId,
              restfulPlaceholders: { entId },
              params: {},
            },
          ]),
          sendBatch([
            {
              apiKey: "/ents/:entId/license/dashboard.read",
              requestUuid: mspDashboardRequestId,
              restfulPlaceholders: { entId },
              params: {},
            },
            {
              apiKey: "/ents/:entId/license/history.read",
              requestUuid: mspHistoryRequestId,
              restfulPlaceholders: { entId },
              params: {},
            },
          ]),
        ]);
        const summary =
          summaryResponses.getSendBatchData({
            requestUuid: orgSummaryRequestId,
          })?.data || [];
        setOverview({
          organizations: summary.map((org) => {
            const proSites = (org.sites || []).filter(
              (site) => site.services?.unityPro === true,
            );

            return {
              id: org.orgId,
              name: org.orgName,
              subscriptionStatus: proSites.length ? "Active" : "Inactive",
              billableDevices: proSites.reduce(
                (total, site) =>
                  total +
                  (Number(site.accessPoints) || 0) +
                  (Number(site.switches) || 0),
                0,
              ),
              balance: org.balance || 0,
              dailyUsage: org.dailyUsage || 0,
              alert: getUsageAlert({
                alertStatus: org.alertStatus,
                dailyUsage: Number(org.dailyUsage) || 0,
                remainingDays: org.remainingDays,
                hasSubscription: proSites.length > 0,
              }),
              daysLeft: org.remainingDays,
            };
          }),
          emulatorOrganizations: summary,
        });
        const mspDashboard = mspResponses.getSendBatchData({
          requestUuid: mspDashboardRequestId,
        });
        const mspHistory = mspResponses.getSendBatchData({
          requestUuid: mspHistoryRequestId,
        });

        if (mspDashboard?.code === 403) {
          // Falcon does not currently expose the MSP-level dashboard endpoint.
          // Keep the Wallet UI visible with a zero balance until that API exists.
          setDashboard({ balance: 0 });
        } else if (mspDashboard?.data) {
          setDashboard(mspDashboard.data);
        }

        if (Array.isArray(mspHistory?.data)) setHistory(mspHistory.data);
      } catch {
        setError("Unable to read the selected MSP.");
      } finally {
        setLoadingOverview(false);
      }
    },
    [scopeReady, sendBatch],
  );

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const getMspImportRequest = () => ({
    apiKey: "/ents/:entId/license/add.create",
    params: { entId: getMspEntId() },
  });

  const handleImportComplete = () => {
    fetchOverview(true);
  };

  const organizations = overview?.organizations || [];
  const treasuryBalance = dashboard?.balance;

  return (
    <Box className={licenseLayoutStyles.mainArea}>
      <Box className={licenseLayoutStyles.anchorArea}>
        <Box className={licenseLayoutStyles.anchorBody}>
          {menuItems.map((item) => {
            const selected = nowAnchor === item.id;
            return (
              <Box
                key={item.id}
                onClick={() => setNowAnchor(item.id)}
                className={licenseLayoutStyles.anchorItem}
                sx={{
                  backgroundColor: selected
                    ? theme.palette.primary.main
                    : "transparent",
                }}
              >
                <img
                  src={item.icon}
                  alt=''
                  style={{
                    width: "28px",
                    filter: selected ? "brightness(0) invert(1)" : "none",
                  }}
                />
                <span
                  className={licenseLayoutStyles.anchorItem_text}
                  style={{
                    color: selected
                      ? theme.palette.common.white
                      : theme.palette.primary.main,
                  }}
                >
                  {item.label}
                </span>
              </Box>
            );
          })}
        </Box>
      </Box>
      <Box className={licenseLayoutStyles.userArea}>
        <Box className={licenseLayoutStyles.contentArea}>
          {loadingOverview ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 240,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={36} />
            </Box>
          ) : (
            <>
              {error && <Alert severity='error'>{error}</Alert>}
              {!loadingOverview && nowAnchor === "overview" && (
                <Overview
                  dashboard={dashboard}
                  transactions={history}
                  mspName={mspName}
                  onShowTransactions={() => setNowAnchor("transactions")}
                  onImportCredits={() => setOpenImportModal(true)}
                />
              )}
              {!loadingOverview && nowAnchor === "transactions" && (
                <Box>
                  <Stack
                    direction='row'
                    justifyContent='space-between'
                    alignItems='center'
                    sx={{ mb: 3, height: "64px", mt: -1 }}
                  >
                    <Typography
                      variant='h4'
                      sx={{
                        color: "#172664",
                        fontFamily: theme.typography.fontFamily,
                        fontSize: "20px",
                        fontWeight: "bold",
                      }}
                    >
                      Transactions
                    </Typography>
                  </Stack>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      flexGrow: 1,
                    }}
                  >
                    <RecentTransactionsBlock
                      transactions={history}
                      mspName={mspName}
                      showControls
                      hideTitle
                      onRefresh={() => fetchOverview(true)}
                    />
                  </Box>
                </Box>
              )}
              {!loadingOverview && nowAnchor === "usage-emulator" && (
                <UsageEmulator
                  dashboard={dashboard}
                  managedOrganizations={overview?.emulatorOrganizations}
                />
              )}
              {!loadingOverview &&
                nowAnchor !== "wallet-disbursement" &&
                nowAnchor !== "overview" &&
                nowAnchor !== "transactions" &&
                nowAnchor !== "usage-emulator" && (
                  <Paper
                    elevation={0}
                    sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}
                  >
                    <Typography variant='h5'>
                      {menuItems.find((item) => item.id === nowAnchor)?.label}
                    </Typography>
                  </Paper>
                )}
              {!loadingOverview && nowAnchor === "wallet-disbursement" && (
                <Box className={styles.page}>
                  {!error && overview && (
                    <>
                      {dashboard && (
                        <MainCard
                          content={false}
                          sx={{
                            bgcolor: theme.palette.common.white,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: "15px",
                            boxShadow: "none",
                            overflow: "hidden",
                          }}
                        >
                          <Box sx={{ p: 4 }}>
                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              justifyContent='space-between'
                              alignItems={{ xs: "flex-start", sm: "center" }}
                              spacing={3}
                            >
                              <Box>
                                <Typography
                                  variant='body2'
                                  sx={{
                                    color: theme.palette.text.secondary,
                                    fontSize:
                                      theme.typography.customFontSize[1],
                                    fontWeight:
                                      theme.typography.customFontWeight[1],
                                  }}
                                >
                                  MSP treasury (disbursement vault)
                                </Typography>
                                <Box className={styles.balance}>
                                  <Typography
                                    component='span'
                                    sx={{
                                      color: theme.palette.primary.greenPrimary,
                                      fontSize:
                                        theme.typography.customFontSize[6],
                                      fontWeight:
                                        theme.typography.customFontWeight[1],
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    {formatNumber(treasuryBalance)}
                                  </Typography>
                                  <Typography
                                    component='span'
                                    sx={{
                                      color: theme.palette.text.secondary,
                                      fontSize:
                                        theme.typography.customFontSize[1],
                                      fontWeight:
                                        theme.typography.customFontWeight[1],
                                    }}
                                  >
                                    credits
                                  </Typography>
                                </Box>
                              </Box>
                            </Stack>
                          </Box>
                        </MainCard>
                      )}

                      <TableContainer
                        component={Box}
                        sx={{
                          border: "none",
                          boxShadow: "none",
                        }}
                      >
                        <Table
                          aria-label='MSP organization license management'
                          sx={{
                            "& .MuiTableCell-head": {
                              borderBottom: `1px solid ${theme.palette.divider}`,
                              color: "#172664",
                              fontWeight: "bold",
                              fontSize: "14px",
                              pb: 2,
                            },
                            "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)":
                              { bgcolor: "#F9F9F9" },
                            "& .MuiTableBody-root .MuiTableCell-root": {
                              border: "none",
                              py: 1.5,
                            },
                            "& .MuiTableCell-root:first-of-type": { pl: 0 },
                            "& .MuiTableCell-root:last-of-type": { pr: 0 },
                          }}
                        >
                          <TableHead>
                            <TableRow>
                              <TableCell>Organization</TableCell>
                              <TableCell>Subscription</TableCell>
                              <TableCell align='right'>
                                Billable Devices
                              </TableCell>
                              <TableCell align='right'>
                                Balance (Credits)
                              </TableCell>
                              <TableCell align='right'>Daily Usage</TableCell>
                              <TableCell>Alerts</TableCell>
                              <TableCell align='right'>Days left</TableCell>
                              <TableCell align='right'>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {organizations.map((organization) => (
                              <TableRow key={organization.id}>
                                <TableCell className={styles.organizationCell}>
                                  <Box
                                    className={styles.organizationName}
                                    sx={{
                                      color: theme.palette.primary.greyPrimary,
                                    }}
                                  >
                                    <EllipsisTooltipCell
                                      text={organization.name}
                                    />
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <span
                                    className={styles.subscriptionStatus}
                                    style={{
                                      color: theme.palette.primary.greyPrimary,
                                    }}
                                  >
                                    {organization.subscriptionStatus}
                                  </span>
                                </TableCell>
                                <TableCell align='right'>
                                  {formatNumber(organization.billableDevices)}
                                </TableCell>
                                <TableCell align='right'>
                                  {formatNumber(organization.balance)}
                                </TableCell>
                                <TableCell align='right'>
                                  {formatNumber(organization.dailyUsage)}{" "}
                                  Credits/Day
                                </TableCell>
                                <TableCell>
                                  <span
                                    style={{
                                      color: theme.palette.primary.greyPrimary,
                                      fontWeight: [
                                        "Predictive Alert",
                                        "Critical Alert",
                                        "Low Balance",
                                        "Grace Period",
                                        "No Subscription",
                                      ].includes(organization.alert)
                                        ? 700
                                        : 400,
                                    }}
                                  >
                                    {organization.alert}
                                  </span>
                                </TableCell>
                                <TableCell align='right'>
                                  {organization.daysLeft == null
                                    ? "-"
                                    : formatNumber(organization.daysLeft)}
                                </TableCell>
                                <TableCell align='right'>
                                  <Box className={styles.actions}>
                                    {actionItems.map(
                                      ({
                                        id,
                                        label,
                                        icon: Icon,
                                        transferDirection,
                                      }) => (
                                        <Tooltip key={id} title={label}>
                                          <IconButton
                                            aria-label={label}
                                            onClick={() => {
                                              if (transferDirection) {
                                                setTransferTarget({
                                                  organization,
                                                  defaultDirection:
                                                    transferDirection,
                                                });
                                              }

                                              if (id === "export-report") {
                                                setExportTarget(organization);
                                                setOpenExportReportModal(true);
                                              }
                                            }}
                                            size='small'
                                            sx={{
                                              color: theme.palette.primary.main,
                                              "&:hover": {
                                                color:
                                                  theme.palette.primary
                                                    .mainHover,
                                                backgroundColor:
                                                  theme.palette.primary
                                                    .lightBlue,
                                              },
                                            }}
                                          >
                                            <Icon fontSize='small' />
                                          </IconButton>
                                        </Tooltip>
                                      ),
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                            {organizations.length === 0 && (
                              <TableRow>
                                <TableCell
                                  colSpan={8}
                                  align='center'
                                  className={styles.emptyState}
                                  sx={{
                                    color: theme.palette.primary.greyThird,
                                  }}
                                >
                                  No managed organizations found.
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {transferTarget && (
                        <TransferCreditsModal
                          open={Boolean(transferTarget)}
                          onClose={() => setTransferTarget(null)}
                          organization={transferTarget.organization}
                          treasuryBalance={treasuryBalance || 0}
                          entId={getMspEntId()}
                          defaultDirection={transferTarget.defaultDirection}
                          onCompleted={() => fetchOverview(true)}
                        />
                      )}

                      <ExportReportModal
                        open={openExportReportModal && Boolean(exportTarget)}
                        onClose={() => {
                          setOpenExportReportModal(false);
                          setExportTarget(null);
                        }}
                        entId={getMspEntId()}
                        organization={exportTarget}
                      />
                    </>
                  )}
                </Box>
              )}
              {!loadingOverview && (
                <ImportCreditsModal
                  auth={{ realm: {}, orgs: {} }}
                  openModal={openImportModal}
                  handleCloseModal={() => setOpenImportModal(false)}
                  onSaveChanges={handleImportComplete}
                  getRequestParams={getMspImportRequest}
                  reloadAfterClose
                />
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MspLicenseManagement;
